import * as React from "react";

import { cn } from "@/lib/utils";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

/**
 * Textareas default to `enterKeyHint="done"` so that on touch devices
 * the native Return key renders as the localized blue "Done" / confirm
 * button (Done / Hecho / Fine / etc., matching the device language).
 *
 * Unlike a real Enter key, pressing Done in our textareas does NOT
 * insert a newline — it blurs the field, which dismisses the on-screen
 * keyboard on iOS Safari and Android Chrome. If the textarea is inside
 * a form, the form is submitted first so existing save handlers still
 * run.
 *
 * Callers can override by passing their own `enterKeyHint` and/or
 * `onKeyDown`. Caller-provided `onKeyDown` runs first and can
 * `preventDefault()` to opt out of the dismiss-on-Enter behavior
 * (e.g. a chat composer that wants Shift+Enter for newlines and
 * Enter for send).
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, enterKeyHint, onKeyDown, ...props }, ref) => {
    const resolvedHint = enterKeyHint ?? "done";

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Always run caller-provided handler first so overrides win.
        onKeyDown?.(e);
        if (e.defaultPrevented) return;

        // Only react to a plain Enter press.
        if (e.key !== "Enter") return;
        if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;

        const el = e.currentTarget;
        // Suppress the newline that the textarea would normally insert.
        e.preventDefault();

        const form = el.form;
        if (form) {
          form.requestSubmit();
        }
        el.blur();
        if (typeof window !== "undefined") {
          window.requestAnimationFrame(() => {
            try {
              el.blur();
            } catch {
              /* ignore */
            }
          });
        }
      },
      [onKeyDown],
    );

    return (
      <textarea
        enterKeyHint={resolvedHint}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex min-h-[80px] w-full rounded-xl border border-[#b6dded] bg-white shadow-sm px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[#2B7DE0] disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
